import {
	DndContext,
	DragEndEvent,
	DragMoveEvent,
	DragOverlay,
	DragStartEvent,
	KeyboardSensor,
	PointerSensor,
	useDraggable,
	useSensor,
	useSensors,
} from "@dnd-kit/core";
import { GripVertical } from "lucide-react";
import React, {
	CSSProperties,
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { cn } from "@/lib/utils";
import { MAX_PER_ROW, ReportRow, WidgetSpec, widgetHeight } from "../../../models/ReportBuilder";
import { DropTarget, normalizeRows } from "./rowLayout";

/** Gap between widgets and between rows, px (Tailwind gap-4). */
const GAP = 16;
/** Widgets stack to one column below this width; dragging is off there. */
const STACK_BELOW = 768;

interface RowsWidget {
	id: string;
	spec: Pick<WidgetSpec, "title" | "chart">;
	note?: string;
}

interface ReportRowsProps {
	rows: ReportRow[];
	widgets: RowsWidget[];
	/** Drag on; off = static read mode. */
	editable: boolean;
	/**
	 * Fit the report to the height the container is given instead of the
	 * widgets' natural heights, so it fits a screen without scrolling (TV
	 * mode). Rows of tiles (numbers, notes) take the height their content
	 * needs; rows with charts or tables share what is left, in proportion,
	 * however small. Widgets can compact themselves under `[data-fit]`.
	 */
	fit?: boolean;
	/** With `fit`: number tiles show the title beside the number (see useReportFit). */
	condensed?: boolean;
	onDrop?: (target: DropTarget) => void;
	renderWidget: (id: string) => React.ReactNode;
	/** Text on the chip that follows the pointer while a widget is dragged. */
	dragLabel?: (id: string) => string;
}

/** Drag-handle wiring a widget card spreads on its grip; undefined outside edit mode. */
export interface WidgetDragHandle {
	ref: (node: HTMLElement | null) => void;
	attributes: Record<string, unknown>;
	listeners: Record<string, unknown> | undefined;
}

const DragHandleContext = createContext<WidgetDragHandle | undefined>(undefined);

/** How the rows are being fitted, for the widget cards inside them. */
export interface ReportFit {
	/** Fitted to the screen (TV mode). */
	fit: boolean;
	/** Number tiles should lay title and number side by side to save height. */
	condensed: boolean;
}

const FitContext = createContext<ReportFit>({ fit: false, condensed: false });

export const useReportFit = (): ReportFit => useContext(FitContext);

export const useWidgetDragHandle = (): WidgetDragHandle | undefined =>
	useContext(DragHandleContext);

const COLS: Record<number, string> = {
	1: "md:grid-cols-1",
	2: "md:grid-cols-2",
	3: "md:grid-cols-3",
	4: "md:grid-cols-4",
};

const useIsWide = (): boolean => {
	const query = `(min-width: ${STACK_BELOW}px)`;
	const [wide, setWide] = useState(() =>
		typeof window === "undefined" ? true : window.matchMedia(query).matches
	);
	useEffect(() => {
		const media = window.matchMedia(query);
		const update = () => setWide(media.matches);
		update();
		media.addEventListener("change", update);
		return () => media.removeEventListener("change", update);
	}, [query]);
	return wide;
};

type Active = { kind: "widget"; widgetId: string } | { kind: "row"; rowId: string };

const activeOf = (id: string | number): Active | undefined => {
	const key = String(id);
	if (key.startsWith("widget:")) return { kind: "widget", widgetId: key.slice(7) };
	if (key.startsWith("row:")) return { kind: "row", rowId: key.slice(4) };
	return undefined;
};

/** Where the pointer is: the activator's position plus what dnd-kit has moved since; the dragged box's centre for keyboard drags. */
const pointerOf = (event: DragMoveEvent): { x: number; y: number } | undefined => {
	const activator = event.activatorEvent as
		| {
				clientX?: number;
				clientY?: number;
				touches?: ArrayLike<{ clientX: number; clientY: number }>;
		  }
		| undefined;
	const start =
		typeof activator?.clientX === "number" && typeof activator.clientY === "number"
			? { x: activator.clientX, y: activator.clientY }
			: activator?.touches?.[0]
			? { x: activator.touches[0].clientX, y: activator.touches[0].clientY }
			: undefined;
	if (start) return { x: start.x + event.delta.x, y: start.y + event.delta.y };
	const rect = event.active.rect.current.translated;
	return rect ? { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 } : undefined;
};

/** The band along a row's top and bottom edge that counts as the gap next to it. */
const edgeOf = (rect: DOMRect): number => Math.min(48, Math.max(16, rect.height * 0.25));

const targetKey = (t: DropTarget | undefined): string =>
	!t
		? ""
		: t.kind === "slot"
		? `slot:${t.rowId}:${t.index}`
		: t.kind === "new-row"
		? `new-row:${t.rowIndex}`
		: `row:${t.rowIndex}`;

interface Line {
	left: number;
	top: number;
	width: number;
	height: number;
}

/**
 * The report as rows of equally wide widgets. Dragging moves nothing until
 * release: a single insertion line shows the one slot (between two widgets,
 * or between two rows) the drop will use, so what is shown is what happens.
 * Full rows offer no slot between their widgets. Below STACK_BELOW the rows
 * stack to one column and cannot be edited.
 */
const ReportRows: React.FC<ReportRowsProps> = ({
	rows: rawRows,
	widgets,
	editable,
	fit = false,
	condensed = false,
	onDrop,
	renderWidget,
	dragLabel,
}) => {
	const byId = useMemo(() => new Map(widgets.map((w) => [w.id, w])), [widgets]);
	const rows = useMemo(
		() =>
			normalizeRows(
				rawRows,
				widgets.map((w) => w.id)
			),
		[rawRows, widgets]
	);
	const wide = useIsWide();
	const canDrag = editable && wide;
	const fitting = useMemo<ReportFit>(
		() => ({ fit, condensed: fit && condensed }),
		[fit, condensed]
	);

	const containerRef = useRef<HTMLDivElement>(null);
	const rowRefs = useRef(new Map<string, HTMLElement>());
	const cellRefs = useRef(new Map<string, HTMLElement>());
	const [active, setActive] = useState<Active | undefined>();
	const [target, setTarget] = useState<DropTarget | undefined>();
	const [line, setLine] = useState<Line | undefined>();
	const lastPointer = useRef<{ x: number; y: number }>();

	const sensors = useSensors(
		useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
		useSensor(KeyboardSensor)
	);

	/** Resolves the pointer to a drop target and the line that shows it. */
	const resolve = useCallback(
		(who: Active, point: { x: number; y: number }): { target?: DropTarget; line?: Line } => {
			const container = containerRef.current;
			if (!container) return {};
			const origin = container.getBoundingClientRect();
			const rects = rows.map((row) => ({
				row,
				rect: rowRefs.current.get(row.id)?.getBoundingClientRect(),
			}));
			if (rects.some((r) => !r.rect)) return {};
			const rowRects = rects as { row: ReportRow; rect: DOMRect }[];

			const gapLine = (gap: number): Line => {
				const y =
					gap === 0
						? rowRects[0].rect.top - GAP / 2
						: gap >= rowRects.length
						? rowRects[rowRects.length - 1].rect.bottom + GAP / 2
						: (rowRects[gap - 1].rect.bottom + rowRects[gap].rect.top) / 2;
				return { left: 0, top: y - origin.top - 2, width: origin.width, height: 4 };
			};

			if (who.kind === "row") {
				const from = rows.findIndex((r) => r.id === who.rowId);
				let gap = rowRects.length;
				for (let i = 0; i < rowRects.length; i++) {
					const { top, bottom } = rowRects[i].rect;
					if (point.y < (top + bottom) / 2) {
						gap = i;
						break;
					}
				}
				if (gap === from || gap === from + 1) return {};
				return { target: { kind: "row", rowId: who.rowId, rowIndex: gap }, line: gapLine(gap) };
			}

			// A widget: the edge bands of every row are gaps, the middle is a slot in that row.
			let gap: number | undefined = rowRects.length;
			let inside: number | undefined;
			for (let i = 0; i < rowRects.length; i++) {
				const { rect } = rowRects[i];
				const edge = edgeOf(rect);
				if (point.y < rect.top + edge) {
					gap = i;
					break;
				}
				if (point.y <= rect.bottom - edge) {
					inside = i;
					gap = undefined;
					break;
				}
			}
			const from = rows.findIndex((r) => r.widgets.includes(who.widgetId));
			const alone = from >= 0 && rows[from].widgets.length === 1;
			if (gap !== undefined) {
				if (alone && (gap === from || gap === from + 1)) return {};
				return {
					target: { kind: "new-row", widgetId: who.widgetId, rowIndex: gap },
					line: gapLine(gap),
				};
			}
			if (inside === undefined) return {};
			const { row, rect } = rowRects[inside];
			const sameRow = inside === from;
			if (alone && sameRow) return {};
			if (!sameRow && row.widgets.length >= MAX_PER_ROW) return {};
			const cells = row.widgets.map((id) => cellRefs.current.get(id)?.getBoundingClientRect());
			if (cells.some((c) => !c)) return {};
			const cellRects = cells as DOMRect[];
			const index = cellRects.filter((c) => c.left + c.width / 2 < point.x).length;
			if (sameRow) {
				const own = row.widgets.indexOf(who.widgetId);
				if (index === own || index === own + 1) return {};
			}
			const x =
				index === 0
					? rect.left
					: index >= cellRects.length
					? rect.right
					: (cellRects[index - 1].right + cellRects[index].left) / 2;
			return {
				target: { kind: "slot", widgetId: who.widgetId, rowId: row.id, index },
				line: {
					left: x - origin.left - 2,
					top: rect.top - origin.top,
					width: 4,
					height: rect.height,
				},
			};
		},
		[rows]
	);

	const update = useCallback(
		(who: Active, point: { x: number; y: number }) => {
			lastPointer.current = point;
			const next = resolve(who, point);
			setLine(next.line);
			setTarget((prev) => (targetKey(prev) === targetKey(next.target) ? prev : next.target));
		},
		[resolve]
	);

	// Autoscroll moves the rows under a still pointer; re-resolve on scroll while dragging.
	useEffect(() => {
		if (!active) return;
		const onScroll = () => {
			if (lastPointer.current) update(active, lastPointer.current);
		};
		window.addEventListener("scroll", onScroll, true);
		return () => window.removeEventListener("scroll", onScroll, true);
	}, [active, update]);

	const onDragStart = (event: DragStartEvent) => {
		setActive(activeOf(event.active.id));
		setTarget(undefined);
		setLine(undefined);
	};

	const onDragMove = (event: DragMoveEvent) => {
		const who = activeOf(event.active.id);
		const point = pointerOf(event);
		if (who && point) update(who, point);
	};

	const finish = () => {
		setActive(undefined);
		setTarget(undefined);
		setLine(undefined);
		lastPointer.current = undefined;
	};

	const onDragEnd = (_event: DragEndEvent) => {
		const dropped = target;
		finish();
		if (dropped) onDrop?.(dropped);
	};

	const overlayLabel =
		active?.kind === "widget"
			? dragLabel?.(active.widgetId) ?? byId.get(active.widgetId)?.spec.title ?? ""
			: active?.kind === "row"
			? `Rad med ${rows.find((r) => r.id === active.rowId)?.widgets.length ?? 0} widgets`
			: "";

	return (
		<FitContext.Provider value={fitting}>
			<DndContext
				sensors={sensors}
				collisionDetection={() => []}
				onDragStart={onDragStart}
				onDragMove={onDragMove}
				onDragEnd={onDragEnd}
				onDragCancel={finish}
			>
				<div
					ref={containerRef}
					data-fit={fit || undefined}
					className={cn(
						"relative flex flex-col gap-4",
						fit && "min-h-0 flex-1",
						canDrag && "report-rows-editing select-none pl-6",
						// While dragging, charts must not answer hover under the pointer.
						active && "[&_.grid>div]:pointer-events-none"
					)}
				>
					{rows.map((row) => {
						const members = row.widgets.map((id) => byId.get(id)).filter(Boolean) as RowsWidget[];
						const rowH = Math.max(0, ...members.map(widgetHeight));
						const tiles = members.every(
							(w) => w.spec.chart.type === "number" || typeof w.note === "string"
						);
						const allTiles =
							members.length > 1 &&
							members.every((w) => w.spec.chart.type === "number" && typeof w.note !== "string");
						return (
							<RowView
								key={row.id}
								row={row}
								cols={COLS[Math.min(MAX_PER_ROW, Math.max(1, members.length))]}
								stackCols={allTiles ? "grid-cols-2" : "grid-cols-1"}
								height={rowH}
								fit={fit ? (tiles ? "content" : "share") : undefined}
								draggable={canDrag}
								dimmed={active?.kind === "row" && active.rowId === row.id}
								setRef={(el) => {
									if (el) rowRefs.current.set(row.id, el);
									else rowRefs.current.delete(row.id);
								}}
							>
								{members.map((widget) => (
									<WidgetCell
										key={widget.id}
										widgetId={widget.id}
										ownHeight={widgetHeight(widget)}
										rowHeight={rowH}
										fit={fit}
										draggable={canDrag}
										dimmed={active?.kind === "widget" && active.widgetId === widget.id}
										setRef={(el) => {
											if (el) cellRefs.current.set(widget.id, el);
											else cellRefs.current.delete(widget.id);
										}}
									>
										{renderWidget(widget.id)}
									</WidgetCell>
								))}
							</RowView>
						);
					})}
					{line && (
						<div
							className="pointer-events-none absolute z-10 rounded-full bg-action shadow-[0_0_0_2px_rgba(255,255,255,0.9)]"
							style={line as CSSProperties}
						/>
					)}
				</div>
				<DragOverlay dropAnimation={null}>
					{active && (
						<div className="pointer-events-none inline-flex max-w-xs items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-800 shadow-lg">
							<GripVertical className="h-4 w-4 shrink-0 text-gray-400" />
							<span className="truncate">{overlayLabel}</span>
						</div>
					)}
				</DragOverlay>
			</DndContext>
		</FitContext.Provider>
	);
};

interface RowViewProps {
	row: ReportRow;
	cols: string;
	stackCols: string;
	/** The row's natural height in px; also its weight when it shares height. */
	height: number;
	/** Fitting: take the content's height, or share the leftover height with the other sharing rows. */
	fit?: "content" | "share";
	draggable: boolean;
	dimmed: boolean;
	setRef: (el: HTMLElement | null) => void;
	children: React.ReactNode;
}

const RowView: React.FC<RowViewProps> = ({
	row,
	cols,
	stackCols,
	height,
	fit,
	draggable,
	dimmed,
	setRef,
	children,
}) => {
	const { attributes, listeners, setNodeRef, setActivatorNodeRef } = useDraggable({
		id: `row:${row.id}`,
		disabled: !draggable,
	});
	return (
		<div
			ref={(el) => {
				setNodeRef(el);
				setRef(el);
			}}
			style={
				fit === "content"
					? { flexGrow: 0, flexShrink: 0, flexBasis: "auto" }
					: fit === "share"
					? { flexGrow: height, flexShrink: 1, flexBasis: 0 }
					: undefined
			}
			className={cn(
				"relative grid gap-4",
				stackCols,
				cols,
				// Tracks as tall as the row, not its content, so the cells can shrink.
				fit && "min-h-0 auto-rows-[minmax(0,1fr)]",
				dimmed && "opacity-40"
			)}
		>
			{draggable && (
				<button
					type="button"
					ref={setActivatorNodeRef}
					{...attributes}
					{...listeners}
					className="absolute -left-6 top-1/2 flex h-10 w-6 -translate-y-1/2 cursor-grab items-center justify-center rounded text-gray-300 hover:bg-gray-100 hover:text-gray-600 active:cursor-grabbing"
					title="Dra för att flytta raden"
					aria-label="Flytta raden"
				>
					<GripVertical className="h-4 w-4" />
				</button>
			)}
			{children}
		</div>
	);
};

interface WidgetCellProps {
	widgetId: string;
	ownHeight: number;
	rowHeight: number;
	fit: boolean;
	draggable: boolean;
	dimmed: boolean;
	setRef: (el: HTMLElement | null) => void;
	children: React.ReactNode;
}

const WidgetCell: React.FC<WidgetCellProps> = ({
	widgetId,
	ownHeight,
	rowHeight,
	fit,
	draggable,
	dimmed,
	setRef,
	children,
}) => {
	const { attributes, listeners, setNodeRef, setActivatorNodeRef } = useDraggable({
		id: `widget:${widgetId}`,
		disabled: !draggable,
	});
	const handle = useMemo<WidgetDragHandle | undefined>(
		() =>
			draggable
				? {
						ref: setActivatorNodeRef,
						attributes: attributes as unknown as Record<string, unknown>,
						listeners,
				  }
				: undefined,
		[draggable, setActivatorNodeRef, attributes, listeners]
	);
	const style = {
		"--h-own": `${ownHeight}px`,
		"--h-row": `${rowHeight}px`,
	} as CSSProperties;
	return (
		<div
			ref={(el) => {
				setNodeRef(el);
				setRef(el);
			}}
			style={style}
			className={cn(
				"min-w-0",
				fit ? "h-full min-h-0 overflow-hidden rounded-lg" : "h-[var(--h-own)] md:h-[var(--h-row)]",
				dimmed && "opacity-40 transition-opacity"
			)}
		>
			<DragHandleContext.Provider value={handle}>{children}</DragHandleContext.Provider>
		</div>
	);
};

export default ReportRows;
